import { useTheme } from '@emotion/react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Modal,
  Select,
  styled,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { H3, H5, Small } from 'components/Typography';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { ChevronDown, ChevronLeft } from 'lucide-react';
import { useState } from 'react';

// Custom styled MenuItem to override purple colors
const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.grey10,
    color: '#151515',
    '&:hover': {
      backgroundColor: theme.palette.primary.grey10,
    },
  },
  '&:hover': {
    backgroundColor: theme.palette.primary.grey10,
  },
}));

export default function CompanyInfoModal({ showModal, setShowModal }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // State for certifications
  const [certifications, setCertifications] = useState({
    researchCenter: false,
    venture: false,
    innobiz: false,
    mainbiz: false,
    womenOwned: false,
    jobCreation: true,
    smartFactory: true,
    exportCompany: true,
    socialEnterprise: true,
    industrialDesign: true,
  });

  // State for business sectors
  const [businessSectors, setBusinessSectors] = useState({
    ai: false,
    fashion: false,
    manufacturing: false,
    cosmetics: false,
    robotics: false,
    smallBusiness: true,
    content: false,
    pets: false,
    culture: false,
  });

  // State for dropdowns
  const [patentStatus, setPatentStatus] = useState('applying');
  const [exportStatus, setExportStatus] = useState('noRecord');

  // State for general company information
  const [companyInfo, setCompanyInfo] = useState({
    companyName: '한솔농산',
    businessRegistrationNumber: '594-96-01248',
    establishmentDate: dayjs('2020-08-27'),
    businessType: '',
    numberOfEmployees: '4',
    kcbCreditScore: '998',
    niceCreditScore: '980',
  });

  const handleClose = () => {
    setShowModal(false);
  };

  const handleCertificationChange = certification => {
    setCertifications(prev => ({
      ...prev,
      [certification]: !prev[certification],
    }));
  };

  const handleBusinessSectorChange = sector => {
    setBusinessSectors(prev => ({
      ...prev,
      [sector]: !prev[sector],
    }));
  };

  const handleCompanyInfoChange = (field, value) => {
    setCompanyInfo(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: isMobile ? '95%' : isTablet ? '90%' : '800px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.primary.white,
    boxShadow: '0px 0px 24px rgba(0, 0, 0, 0.2)',
    outline: 'none',
    borderRadius: '24px',
    overflow: 'hidden',
  };

  const CheckboxItem = ({ checked, onChange, label }) => (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={onChange}
          sx={{
            color: '#4285F4',
            '&.Mui-checked': {
              color: '#4285F4',
            },
            '&:not(.Mui-checked)': {
              color: theme.palette.primary.grey10,
              backgroundColor: theme.palette.primary.grey10,
              border: 'none',
              borderRadius: '4px',
            },
            '& .MuiSvgIcon-root': {
              fontSize: 20,
              borderRadius: '4px',
            },
          }}
        />
      }
      label={
        <Small
          sx={{
            fontSize: '14px',
            fontWeight: '500',
            color: '#151515',
            marginLeft: '8px',
          }}
        >
          {label}
        </Small>
      }
      sx={{
        margin: 0,
        '& .MuiFormControlLabel-label': {
          fontSize: '14px',
        },
      }}
    />
  );

  return (
    <Modal
      open={showModal}
      onClose={handleClose}
      aria-labelledby="company-info-modal"
      style={{ backdropFilter: 'blur(5px)' }}
    >
      <Box sx={modalStyle}>
        {/* Sticky Header */}
        <Box
          sx={{
            padding: isMobile ? '12px' : '20px',
            paddingBottom: isMobile ? '12px' : '16px',
            position: 'sticky',
            top: 0,
            backgroundColor: theme.palette.primary.white,
            zIndex: 10,
            borderBottom: '1px solid #F0F0F0',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ChevronLeft
                size={24}
                color={theme.palette.primary.greytxt}
                style={{ marginRight: '12px', cursor: 'pointer' }}
                onClick={handleClose}
              />
              <H3
                sx={{ fontSize: '18px', fontWeight: '600', color: '#151515' }}
              >
                내 기업정보
              </H3>
            </Box>
            <Button
              onClick={handleClose}
              sx={{
                backgroundColor: '#4285F4',
                color: 'white',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#3367D6',
                },
              }}
            >
              저장
            </Button>
          </Box>
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            padding: isMobile ? '20px' : '32px',
            paddingTop: isMobile ? '20px' : '32px',
            overflowY: 'auto',
            flex: 1,
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '0.4em',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'transparent',
            },
          }}
        >
          {/* Greeting and Progress */}
          <Box sx={{ marginBottom: '24px' }}>
            <H3
              sx={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#151515',
                marginBottom: '8px',
              }}
            >
              홍길동 대표님!
            </H3>
            <Small
              sx={{
                fontSize: '14px',
                color: '#808187',
                lineHeight: 1.5,
                marginBottom: '16px',
                display: 'block',
              }}
            >
              기업정보를 입력하면 AI를 통해 자동으로 검색하고 추천받을 수
              있어요!
            </Small>
            <Box sx={{ width: '100%', marginBottom: '8px' }}>
              <LinearProgress
                variant="determinate"
                value={91}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#E0E0E0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#4285F4',
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '4px',
              }}
            >
              <Small
                sx={{
                  fontSize: '14px',
                  color: '#4285F4',
                  fontWeight: '600',
                }}
              >
                91%
              </Small>
            </Box>
          </Box>

          {/* Content - Combined view */}
          <Box>
            {/* Certification Status */}
            <Box sx={{ marginBottom: '32px' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}
              >
                <H5
                  sx={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#151515',
                    marginBottom: 0,
                    marginRight: '8px',
                  }}
                >
                  보유한 인증 현황
                </H5>
                <Small
                  sx={{
                    fontSize: '13px',
                    color: '#808187',
                    display: 'inline',
                  }}
                >
                  (중복 선택 가능)
                </Small>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={certifications.researchCenter}
                    onChange={() => handleCertificationChange('researchCenter')}
                    label="기업부설연구소"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={certifications.venture}
                    onChange={() => handleCertificationChange('venture')}
                    label="벤처인증"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={certifications.innobiz}
                    onChange={() => handleCertificationChange('innobiz')}
                    label="이노비즈"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={certifications.mainbiz}
                    onChange={() => handleCertificationChange('mainbiz')}
                    label="메인비즈"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={certifications.womenOwned}
                    onChange={() => handleCertificationChange('womenOwned')}
                    label="여성기업"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={certifications.jobCreation}
                    onChange={() => handleCertificationChange('jobCreation')}
                    label="일자리창출 우수기업"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={certifications.smartFactory}
                    onChange={() => handleCertificationChange('smartFactory')}
                    label="스마트공장"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={certifications.exportCompany}
                    onChange={() => handleCertificationChange('exportCompany')}
                    label="수출기업"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={certifications.socialEnterprise}
                    onChange={() =>
                      handleCertificationChange('socialEnterprise')
                    }
                    label="사회적기업"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={certifications.industrialDesign}
                    onChange={() =>
                      handleCertificationChange('industrialDesign')
                    }
                    label="산업디자인전문회사"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Patent Status */}
            <Box sx={{ marginBottom: '32px' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}
              >
                <H5
                  sx={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#151515',
                    marginBottom: 0,
                    marginRight: '8px',
                  }}
                >
                  특허 보유 현황
                </H5>
                <Small
                  sx={{
                    fontSize: '13px',
                    color: '#808187',
                    display: 'inline',
                    marginBottom: 0,
                  }}
                >
                  (상표 제외)
                </Small>
              </Box>
              <FormControl fullWidth>
                <Select
                  value={patentStatus}
                  onChange={e => setPatentStatus(e.target.value)}
                  displayEmpty
                  sx={{
                    backgroundColor: '#F5F5F5',
                    borderRadius: '12px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '& .MuiAvatar-root': {
                      display: 'none',
                    },
                  }}
                  IconComponent={() => (
                    <ChevronDown
                      size={20}
                      color="#808187"
                      style={{ marginRight: '16px' }}
                    />
                  )}
                >
                  <StyledMenuItem value="applying">
                    특허를 출원중이거나 계획이 있어요.
                  </StyledMenuItem>
                  <StyledMenuItem value="none">
                    특허 보유 현황이 없어요.
                  </StyledMenuItem>
                  <StyledMenuItem value="have">
                    특허를 보유하고 있어요.
                  </StyledMenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Export Status */}
            <Box sx={{ marginBottom: '32px' }}>
              <H5
                sx={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#151515',
                  marginBottom: '16px',
                }}
              >
                수출 현황
              </H5>
              <FormControl fullWidth>
                <Select
                  value={exportStatus}
                  onChange={e => setExportStatus(e.target.value)}
                  displayEmpty
                  sx={{
                    backgroundColor: '#F5F5F5',
                    borderRadius: '12px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                  IconComponent={() => (
                    <ChevronDown
                      size={20}
                      color="#808187"
                      style={{ marginRight: '16px' }}
                    />
                  )}
                >
                  <StyledMenuItem value="noRecord">
                    수출실적이 없어요.
                  </StyledMenuItem>
                  <StyledMenuItem value="hasRecord">
                    수출실적이 있어요.
                  </StyledMenuItem>
                  <StyledMenuItem value="planning">
                    수출 계획이 있어요.
                  </StyledMenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Business Sector */}
            <Box>
              <H5
                sx={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#151515',
                  marginBottom: '16px',
                }}
              >
                사업 분야
              </H5>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={businessSectors.ai}
                    onChange={() => handleBusinessSectorChange('ai')}
                    label="인공지능(AI)"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={businessSectors.fashion}
                    onChange={() => handleBusinessSectorChange('fashion')}
                    label="패션/섬유"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={businessSectors.manufacturing}
                    onChange={() => handleBusinessSectorChange('manufacturing')}
                    label="제조"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={businessSectors.cosmetics}
                    onChange={() => handleBusinessSectorChange('cosmetics')}
                    label="화장품/뷰티"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={businessSectors.robotics}
                    onChange={() => handleBusinessSectorChange('robotics')}
                    label="로봇/기계"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={businessSectors.smallBusiness}
                    onChange={() => handleBusinessSectorChange('smallBusiness')}
                    label="소상공인"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={businessSectors.content}
                    onChange={() => handleBusinessSectorChange('content')}
                    label="콘텐츠"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={businessSectors.pets}
                    onChange={() => handleBusinessSectorChange('pets')}
                    label="반려동물"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <CheckboxItem
                    checked={businessSectors.culture}
                    onChange={() => handleBusinessSectorChange('culture')}
                    label="문화/예술/스포츠"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Divider between sections */}
            <Box
              sx={{
                marginTop: '48px',
                marginBottom: '32px',
                borderTop: '1px solid #E0E0E0',
                paddingTop: '32px',
              }}
            >
              <H5
                sx={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#151515',
                  marginBottom: '32px',
                }}
              >
                기업 일반 정보
              </H5>
            </Box>

            {/* Company Name */}
            <Box sx={{ marginBottom: '32px' }}>
              <H5
                sx={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#151515',
                  marginBottom: '16px',
                }}
              >
                상호명 (사업자등록증 기준)
              </H5>
              <TextField
                fullWidth
                value={companyInfo.companyName}
                onChange={e =>
                  handleCompanyInfoChange('companyName', e.target.value)
                }
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused fieldset': {
                      border: '1px solid #4285F4',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    color: '#151515',
                    fontWeight: '500',
                    padding: '16px',
                  },
                }}
              />
            </Box>

            {/* Business Registration Number */}
            <Box sx={{ marginBottom: '32px' }}>
              <H5
                sx={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#151515',
                  marginBottom: '16px',
                }}
              >
                사업자 등록번호
              </H5>
              <TextField
                fullWidth
                value={companyInfo.businessRegistrationNumber}
                onChange={e =>
                  handleCompanyInfoChange(
                    'businessRegistrationNumber',
                    e.target.value
                  )
                }
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused fieldset': {
                      border: '1px solid #4285F4',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    color: '#151515',
                    fontWeight: '500',
                    padding: '16px',
                  },
                }}
              />
            </Box>

            {/* Date of Establishment */}
            <Box sx={{ marginBottom: '32px' }}>
              <H5
                sx={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#151515',
                  marginBottom: '16px',
                }}
              >
                개업 연월일
              </H5>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="ko"
              >
                <DatePicker
                  value={companyInfo.establishmentDate}
                  onChange={newValue =>
                    handleCompanyInfoChange('establishmentDate', newValue)
                  }
                  format="YYYY. MM. DD"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        backgroundColor: '#F5F5F5',
                        borderRadius: '12px',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          '& fieldset': {
                            border: 'none',
                          },
                          '&:hover fieldset': {
                            border: 'none',
                          },
                          '&.Mui-focused fieldset': {
                            border: '1px solid #4285F4',
                          },
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '14px',
                          color: '#151515',
                          fontWeight: '500',
                          padding: '16px',
                        },
                        '& .MuiIconButton-root': {
                          color: '#808187',
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            {/* Business Type */}
            <Box sx={{ marginBottom: '32px' }}>
              <H5
                sx={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#151515',
                  marginBottom: '16px',
                }}
              >
                업종
              </H5>
              <FormControl fullWidth>
                <Select
                  value={companyInfo.businessType}
                  onChange={e =>
                    handleCompanyInfoChange('businessType', e.target.value)
                  }
                  displayEmpty
                  sx={{
                    backgroundColor: '#F5F5F5',
                    borderRadius: '12px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: '1px solid #4285F4',
                    },
                    '& .MuiSelect-select': {
                      fontSize: '14px',
                      color: companyInfo.businessType ? '#151515' : '#808187',
                      fontWeight: '500',
                      padding: '16px',
                    },
                  }}
                  IconComponent={() => (
                    <ChevronDown
                      size={20}
                      color="#808187"
                      style={{ marginRight: '16px' }}
                    />
                  )}
                >
                  <StyledMenuItem value="" disabled>
                    업종을 선택해주세요
                  </StyledMenuItem>
                  <StyledMenuItem value="제조업">제조업</StyledMenuItem>
                  <StyledMenuItem value="도매 및 소매업">
                    도매 및 소매업
                  </StyledMenuItem>
                  <StyledMenuItem value="정보통신업">정보통신업</StyledMenuItem>
                  <StyledMenuItem value="전문, 과학 및 기술 서비스업">
                    전문, 과학 및 기술 서비스업
                  </StyledMenuItem>
                  <StyledMenuItem value="건설업">건설업</StyledMenuItem>
                  <StyledMenuItem value="운수업">운수업</StyledMenuItem>
                  <StyledMenuItem value="숙박 및 음식점업">
                    숙박 및 음식점업
                  </StyledMenuItem>
                  <StyledMenuItem value="교육 서비스업">
                    교육 서비스업
                  </StyledMenuItem>
                  <StyledMenuItem value="예술, 스포츠 및 여가관련 서비스업">
                    예술, 스포츠 및 여가관련 서비스업
                  </StyledMenuItem>
                  <StyledMenuItem value="보건업 및 사회복지 서비스업">
                    보건업 및 사회복지 서비스업
                  </StyledMenuItem>
                  <StyledMenuItem value="농업, 임업 및 어업">
                    농업, 임업 및 어업
                  </StyledMenuItem>
                  <StyledMenuItem value="기타">기타</StyledMenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Number of Employees */}
            <Box sx={{ marginBottom: '32px' }}>
              <H5
                sx={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#151515',
                  marginBottom: '16px',
                }}
              >
                고용 인원 (4대보험 등록 기준)
              </H5>
              <TextField
                fullWidth
                type="number"
                value={companyInfo.numberOfEmployees}
                onChange={e =>
                  handleCompanyInfoChange('numberOfEmployees', e.target.value)
                }
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused fieldset': {
                      border: '1px solid #4285F4',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    color: '#151515',
                    fontWeight: '500',
                    padding: '16px',
                  },
                }}
              />
            </Box>

            {/* Representative Credit Score (KCB) */}
            <Box sx={{ marginBottom: '32px' }}>
              <H5
                sx={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#151515',
                  marginBottom: '16px',
                }}
              >
                대표자 신용점수 (KCB)
              </H5>
              <TextField
                fullWidth
                type="number"
                value={companyInfo.kcbCreditScore}
                onChange={e =>
                  handleCompanyInfoChange('kcbCreditScore', e.target.value)
                }
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused fieldset': {
                      border: '1px solid #4285F4',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    color: '#151515',
                    fontWeight: '500',
                    padding: '16px',
                  },
                }}
              />
            </Box>

            {/* Representative Credit Score (NICE) */}
            <Box>
              <H5
                sx={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#151515',
                  marginBottom: '16px',
                }}
              >
                대표자 신용점수 (NICE)
              </H5>
              <TextField
                fullWidth
                type="number"
                value={companyInfo.niceCreditScore}
                onChange={e =>
                  handleCompanyInfoChange('niceCreditScore', e.target.value)
                }
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused fieldset': {
                      border: '1px solid #4285F4',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    color: '#151515',
                    fontWeight: '500',
                    padding: '16px',
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
